import { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useGraphQLQuery } from 'src/common/hooks';
import Container from '@mui/material/Container';
import { DisplayState } from 'src/containers/client-assessment/types';
import Intro from 'src/containers/client-assessment/Intro';
import Conclusion from 'src/containers/client-assessment/Conclusion';
import Loader from 'src/common/components/Loader';
import ErrorScreen from 'src/common/components/ErrorScreen';
import {
  transformQuestions,
  FETCH_RISK_ASSESSMENT_QUESTIONS,
} from 'src/containers/client-assessment/utils';
import { mobileBreakpoint } from 'src/containers/client-assessment/mobileBreakpoint';
import Sidebar from 'src/containers/client-assessment/Sidebar';
import QuestionnaireMode from 'src/containers/client-assessment/QuestionnaireMode';
import { useQuery } from 'react-query';
import { fetchData } from 'src/common/hooks/fetchData';
import { useQueryParamsWithIds } from 'src/common/hooks/useQueryParamsWithIds';

const MainWrapper = styled.div`
  width: 100%;
  background-color: #f9f9f9;
  min-height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  .firmName {
    margin: 1em 0 0 0;
    font-weight: 400;
    font-size: 14px;
    color: #525863;
  }
  @media ${mobileBreakpoint.mobile} {
    flex-direction: column;
  }
`;

const MainCard = styled.div`
  width: 100%;
  background-color: ${props => props.theme.main.white};
  border-radius: 6px;
  box-shadow: 0px 4px 8px rgba(16, 24, 40, 0.1);
  display: flex;
  align-items: center;
  height: 90vh;
  @media ${mobileBreakpoint.mobile} {
    flex-direction: column;
  }
`;

type StepWrapperProps = {
  fullWidth?: boolean;
  applyPadding?: boolean;
};

const StepWrapper = styled.div`
  width: ${(props: StepWrapperProps) => (props.fullWidth ? '100%' : '70%')};
  display: flex;
  padding: ${props => (props.applyPadding ? '1.5em 2em' : '0')};
  height: 100%;
  // overflow-y: scroll;
  @media ${mobileBreakpoint.mobile} {
    width: 100%;
    padding: ${props => (props.applyPadding ? '0.5em 1em' : '0')};
  }
`;

const ClientAssessment = () => {
  const { query, advisorId, clientId } = useQueryParamsWithIds();

  const {
    isLoading: advisorInfoLoading,
    data: advisorInfo,
    error: advisorInfoError,
  } = useQuery(
    ['advisor-info', advisorId],
    () => fetchData('advisor', advisorId),
    {
      select: res => res?.data ?? {},
      enabled: !!advisorId,
    },
  );

  const {
    isLoading: clientInfoLoading,
    data: clientInfo,
    error: clientInfoError,
  } = useQuery(['client-info', clientId], () => fetchData('client', clientId), {
    select: res => res?.data ?? {},
    enabled: !!clientId,
  });

  const { isLoading, data, isError } = useGraphQLQuery(
    ['assessments_list'],
    {
      query: FETCH_RISK_ASSESSMENT_QUESTIONS,
      headers: {
        'is-sage': true,
        Authorization: process.env.REACT_APP_AUTHORIZATION_TOKEN ?? '',
      },
    },
    {
      select: data => ({
        questions: transformQuestions(data.fetchRiskAssessmentQuestions),
      }),
    },
  );

  const [newQuestions, setNewQuestions] = useState<any[]>([]);
  const [displayState, setDisplayState] = useState<DisplayState>('INTRO');
  const [questionnaireValue, setQuestionnaireValue] = useState<any>({});

  useEffect(() => {
    if (data) {
      let aTempObj = {};
      let prefillData = data?.metadata?.prefillData;
      if (
        data?.metadata?.personsAttributesData &&
        Array.isArray(data?.questions) &&
        Array.isArray(data?.metadata?.personsAttributesData)
      ) {
        data?.metadata?.personsAttributesData?.forEach(e => {
          let questionName = data?.questions?.find(question => {
            return question.attributeKey === e.id;
          });
          if (questionName) {
            aTempObj = {
              ...aTempObj,
              [questionName.questionId]: isNaN(e.value)
                ? e.value
                : parseFloat(e.value),
            };
          }
        });
      }

      data?.questions?.forEach(question => {
        if (
          prefillData?.[question.attributeKey] ||
          prefillData?.[question.attributeKey] === 0
        ) {
          aTempObj = {
            ...aTempObj,
            [question.questionId]: isNaN(prefillData[question.attributeKey])
              ? prefillData[question.attributeKey]
              : parseFloat(prefillData[question.attributeKey]),
          };
        }
      });
      setQuestionnaireValue({ ...questionnaireValue, ...aTempObj });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  useEffect(() => {
    if (query.get('skipIntro')) {
      setDisplayState('QUESTION_MODE');
    }
  }, [query]);

  const metadata = useMemo(() => {
    if (clientInfo && advisorInfo) {
      return {
        clientDetails: {
          firstName: clientInfo?.firstName ?? '',
          lastName: clientInfo?.lastName ?? '',
          email: clientInfo?.email ?? '',
        },
        advisorDetails: advisorInfo,
        firmName: advisorInfo?.advisoryFirmName ?? '',
      };
    }
    return {
      advisorDetails: {
        firstName: '',
        lastName: '',
        email: '',
        firmName: '',
        firmLogo: '',
      },
      clientDetails: {
        firstName: '',
        lastName: '',
        email: '',
      },
      firmName: '',
    };
  }, [advisorInfo, clientInfo]);

  if (!clientId || !advisorId)
    return <h1>Please provide {!advisorId ? 'advisor' : 'client'} id</h1>;

  if (displayState === 'CONCLUSION')
    return <Conclusion advisorInfo={advisorInfo} />;

  if (isLoading || advisorInfoLoading || clientInfoLoading)
    return <Loader maxHeight />;
  if (isError || clientInfoError || advisorInfoError)
    return (
      <ErrorScreen message="Sorry, we’re unable to fetch questionnaire at the moment" />
    );

  return (
    <MainWrapper>
      <Container maxWidth="lg">
        <MainCard>
          {displayState === 'QUESTION_MODE' && (
            <Sidebar
              questions={newQuestions}
              questionnaireValue={questionnaireValue}
              metadata={metadata ?? {}}
            />
          )}
          <StepWrapper
            applyPadding={displayState === 'QUESTION_MODE'}
            fullWidth={displayState !== 'QUESTION_MODE'}
          >
            {displayState === 'QUESTION_MODE' ? (
              <QuestionnaireMode
                questions={data?.questions}
                newQuestions={newQuestions}
                questionnaireValue={questionnaireValue}
                setNewQuestions={setNewQuestions}
                setQuestionnaireValue={setQuestionnaireValue}
                setDisplayState={setDisplayState}
              />
            ) : (
              <Intro
                metadata={metadata ?? {}}
                setDisplayState={setDisplayState}
              />
            )}
          </StepWrapper>
        </MainCard>
      </Container>
      {displayState === 'QUESTION_MODE' && (
        <p className="firmName">{metadata?.advisorDetails?.firmName}</p>
      )}
    </MainWrapper>
  );
};

export default ClientAssessment;
